// src/pages/touchPoints/timeline/edits.js
// Pure, local-state mutations for the editing PROTOTYPE. They operate on the raw
// { journeys, segments, attributeById } shape that deriveModel consumes, returning a
// new object (no in-place mutation), so the canvas re-derives + re-lays-out reactively.
// Nothing here touches the API — edits live in memory only.

let seq = 900000;
const nextId = () => { seq += 1; return seq; };

const newTouchpoint = (journeyId, ordinal, over = {}) => ({
  touchpoint_id: nextId(), journey_id: journeyId, ordinal,
  name: 'New touchpoint', message: '', wait_time: 'P1D',
  response_attribute_id: null, link: null, labels: [], ...over,
});

// Insert a touchpoint immediately after `afterTpId` in its journey. Ordinal is placed
// between that touchpoint and the next (fractional ordinals are fine — deriveModel only
// sorts by them). If it's the last touchpoint, this appends.
export function insertTouchpointAfter(data, journeyId, afterTpId) {
  const journeys = data.journeys.map((j) => {
    if (j.journey_id !== journeyId) return j;
    const sorted = [...j.touchpoints].sort((a, b) => a.ordinal - b.ordinal);
    const idx = sorted.findIndex((t) => t.touchpoint_id === afterTpId);
    const after = sorted[idx];
    const next = sorted[idx + 1];
    const ordinal = next ? (after.ordinal + next.ordinal) / 2 : after.ordinal + 1;
    return { ...j, touchpoints: [...j.touchpoints, newTouchpoint(journeyId, ordinal)] };
  });
  return { ...data, journeys, _lastAddedAt: nextId() };
}

// Append a touchpoint to the end of a journey (after its highest-ordinal touchpoint).
export function appendTouchpoint(data, journeyId) {
  const j = data.journeys.find((x) => x.journey_id === journeyId);
  const last = j && j.touchpoints.length
    ? [...j.touchpoints].sort((a, b) => a.ordinal - b.ordinal).slice(-1)[0]
    : null;
  if (!last) {
    const journeys = data.journeys.map((x) => (x.journey_id === journeyId
      ? { ...x, touchpoints: [...x.touchpoints, newTouchpoint(journeyId, 0)] } : x));
    return { ...data, journeys, _lastAddedAt: nextId() };
  }
  return insertTouchpointAfter(data, journeyId, last.touchpoint_id);
}

// Add a sub-journey branched off `triggerTpId`: a new journey whose entry segment keys
// off the trigger's response attribute. If the trigger has no response attribute yet
// (prototype), fabricate one and stamp it on the trigger so the chain links.
export function addBranch(data, triggerTpId) {
  let respId = null;
  let journeys = data.journeys.map((j) => ({
    ...j,
    touchpoints: j.touchpoints.map((touchpoint) => {
      if (touchpoint.touchpoint_id !== triggerTpId) return touchpoint;
      respId = touchpoint.response_attribute_id != null ? touchpoint.response_attribute_id : `resp-${triggerTpId}`;
      return touchpoint.response_attribute_id != null ? touchpoint : { ...touchpoint, response_attribute_id: respId };
    }),
  }));
  if (respId == null) return data; // trigger not found
  const segId = nextId();
  const jId = nextId();
  const segment = {
    segment_id: segId, name: 'New sub-journey',
    condition: [{ criteria_operator: 'AND', criteria_id_1: [{ type: 'attribute', attribute_id: respId, operator: 'EQUAL', value: ['Yes'] }] }],
  };
  const journey = {
    journey_id: jId, name: 'New sub-journey', state_enum: 'LIVE', is_active: true,
    segment_id: segId, channels: [], labels: [], touchpoints: [newTouchpoint(jId, 0, { wait_time: 'P0D' })],
  };
  journeys = [...journeys, journey];
  return { ...data, journeys, segments: [...data.segments, segment], _lastAddedAt: nextId() };
}
